import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import ExploreHeader from './ExploreHeader';
import TopDashboard from './TopDashboard';
import SearchResults from './SearchResults';
import { searchEntities, fetchTopReferenced, fetchExploreCategories } from '../../lib/exploreApi';
import type {
  ExploreSearchResponse, TopReferencedResponse, TopReferencedParams, ExploreCategoriesResponse,
} from '../../lib/exploreApi';
import { classifyIOC } from '../../lib/iocClassifier';

const IOC_CLASS_TO_CATEGORY: Record<string, string> = {
  IP: 'ip',
  DOMAIN: 'domain',
  HASH: 'hash',
  URL: 'url',
  EMAIL: 'email',
};

const IOC_CLASS_TO_HINT: Record<string, string> = {
  IP: 'IP Address',
  DOMAIN: 'Domain',
  HASH: 'Hash',
  URL: 'URL',
  EMAIL: 'Email',
};

const DEBOUNCE_MS = 300;
const BROWSE_PAGE_SIZE = 15;

export default function ExplorePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQuery = searchParams.get('q') || '';
  const urlCategory = searchParams.get('cat') || '';
  const urlSortBy = searchParams.get('sort') || 'doc_count';
  const urlCountry = searchParams.get('country') || '';
  // Siblings of urlCountry — wired up so Pulse tile click-through lands on
  // a sensible Explore state. ?actor= and ?family= preset both the search
  // box and the category so the page narrows to that entity immediately.
  const urlActor = searchParams.get('actor') || '';
  const urlFamily = searchParams.get('family') || '';
  const urlPage = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = 50;

  const [inputValue, setInputValue] = useState(urlQuery || urlActor || urlFamily);
  const [debouncedQuery, setDebouncedQuery] = useState(urlQuery || urlActor || urlFamily);
  const [detectedHint, setDetectedHint] = useState('');
  // Track whether user manually changed category (don't override their choice)
  const userChangedCategory = useRef(false);

  // Resolve Pulse-tile click-through siblings (?actor=, ?family=, bare
  // ?country=) into the canonical (?q=, ?cat=) state. One-shot on mount —
  // we strip the siblings out so subsequent user actions own the URL.
  useEffect(() => {
    if (!urlActor && !urlFamily && !urlCountry) return;
    const params = new URLSearchParams(searchParams);
    let mutated = false;
    if (urlActor) {
      params.set('q', urlActor);
      params.set('cat', 'actor');
      params.delete('actor');
      mutated = true;
      userChangedCategory.current = true;
    } else if (urlFamily) {
      params.set('q', urlFamily);
      params.set('cat', 'family');
      params.delete('family');
      mutated = true;
      userChangedCategory.current = true;
    } else if (urlCountry && !urlCategory) {
      // Bare ?country= → default to browsing actors of that country.
      params.set('cat', 'actor');
      mutated = true;
      userChangedCategory.current = true;
    }
    if (mutated) setSearchParams(params, { replace: true });
    // Intentionally mount-only — once normalized the URL no longer carries
    // the sibling keys, so subsequent renders skip this block.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce input
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = inputValue.trim();
      setDebouncedQuery(trimmed);

      // Auto-detect IOC type
      const iocClass = trimmed ? classifyIOC(trimmed) : 'UNKNOWN';
      const iocCategory = IOC_CLASS_TO_CATEGORY[iocClass];
      const hint = IOC_CLASS_TO_HINT[iocClass] || '';
      setDetectedHint(hint);

      const params = new URLSearchParams();
      if (trimmed) params.set('q', trimmed);

      if (iocCategory && !userChangedCategory.current) {
        // Auto-set category directly (e.g. cat=hash)
        params.set('cat', iocCategory);
      } else {
        if (urlCategory) params.set('cat', urlCategory);
      }
      if (urlSortBy !== 'doc_count') params.set('sort', urlSortBy);
      setSearchParams(params, { replace: true });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const setCategory = useCallback((cat: string) => {
    userChangedCategory.current = true;
    setDetectedHint('');
    const params = new URLSearchParams(searchParams);
    if (cat) params.set('cat', cat);
    else params.delete('cat');
    params.delete('page');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const setSortBy = useCallback((sort: string) => {
    const params = new URLSearchParams(searchParams);
    if (sort !== 'doc_count') params.set('sort', sort);
    else params.delete('sort');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const setPage = useCallback((page: number) => {
    const params = new URLSearchParams(searchParams);
    if (page > 1) params.set('page', String(page));
    else params.delete('page');
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  // Search query
  const { data: searchData, isLoading: searchLoading } = useQuery<ExploreSearchResponse>({
    queryKey: ['explore-search', debouncedQuery, urlCategory, urlSortBy, urlPage],
    queryFn: () => searchEntities({
      query: debouncedQuery,
      category: urlCategory || undefined,
      sort_by: urlSortBy,
      limit: pageSize,
      offset: (urlPage - 1) * pageSize,
    }),
    enabled: !!debouncedQuery,
  });

  // Category groups — shared queryKey with TopDashboard/ExploreHeader, so
  // this is served from the same cache entry.
  const { data: catsData } = useQuery<ExploreCategoriesResponse>({
    queryKey: ['explore-categories'],
    queryFn: fetchExploreCategories,
    staleTime: 60_000,
  });

  // Only fetch the top lists the dashboard actually renders: none on the
  // landing view (stats banner only), one for a single category, the
  // group's members for a group:<g> selection. null = keys not known yet.
  const topParams = useMemo<TopReferencedParams | null>(() => {
    if (!urlCategory) return { lists: false };
    if (urlCategory.startsWith('group:')) {
      if (!catsData) return null;
      const grp = catsData.groups.find(g => g.key === urlCategory.slice('group:'.length));
      return { categories: grp?.categories.map(c => c.key) ?? [] };
    }
    return { categories: [urlCategory] };
  }, [urlCategory, catsData]);

  // Dashboard query
  const { data: topData, isLoading: topLoading } = useQuery<TopReferencedResponse>({
    queryKey: ['explore-top-referenced', topParams],
    queryFn: () => fetchTopReferenced(topParams ?? undefined),
    enabled: !debouncedQuery && topParams !== null,
    staleTime: 60_000,
    // Keep the stats banner on screen while a new category's lists load.
    placeholderData: keepPreviousData,
  });

  // Browse query: paginated browse when a category tab is selected (no search query)
  const { data: browseData, isLoading: browseLoading } = useQuery<ExploreSearchResponse>({
    queryKey: ['explore-browse', urlCategory, urlSortBy, urlPage, urlCountry],
    queryFn: () => searchEntities({
      query: '',
      category: urlCategory || undefined,
      country: urlCountry || undefined,
      sort_by: urlSortBy,
      limit: BROWSE_PAGE_SIZE,
      offset: (urlPage - 1) * BROWSE_PAGE_SIZE,
    }),
    enabled: !debouncedQuery && !!urlCategory,
    staleTime: 60_000,
  });

  const handleDashboardSearch = useCallback((value: string, category: string) => {
    setInputValue(value);
    setDebouncedQuery(value);
    const params = new URLSearchParams();
    params.set('q', value);
    if (category) params.set('cat', category);
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  const showDashboard = !debouncedQuery;

  return (
    <div className="explore-page">
      <ExploreHeader
        query={inputValue}
        onQueryChange={(v) => {
          if (!v) userChangedCategory.current = false;
          setInputValue(v);
        }}
        category={urlCategory}
        onCategoryChange={setCategory}
        sortBy={urlSortBy}
        onSortByChange={setSortBy}
        detectedHint={detectedHint}
      />

      {showDashboard ? (
        <TopDashboard
          data={topData}
          isLoading={topLoading}
          onItemClick={handleDashboardSearch}
          onCategorySelect={setCategory}
          category={urlCategory}
          browseItems={browseData?.results}
          browseTotal={browseData?.total ?? 0}
          browseLoading={browseLoading}
          page={urlPage}
          pageSize={BROWSE_PAGE_SIZE}
          onPageChange={setPage}
        />
      ) : (
        <SearchResults
          data={searchData}
          isLoading={searchLoading}
          page={urlPage}
          pageSize={pageSize}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
